/**
 * Terms & Conditions — a plain-English T&Cs page for dtscotslad.com.
 *
 * NOTE: this is a sensible default for a personal drone-portfolio site
 * (subscription, bookings, user submissions, general web usage). It's not
 * legal advice — if the site starts handling higher-value transactions or
 * a lot of personal data, get a solicitor to review.
 *
 * Content is text-only + a clean layout that matches the rest of the site.
 * No images, no interactive elements — deliberately quiet so the page is
 * fast to load and easy to reference from the footer.
 */
import PageCTA from "@/components/PageCTA";
import { Reveal, MaskedLine, HighlandBackdrop } from "@/components/Reveal";
import usePageMeta from "@/lib/usePageMeta";

const SITE = "DTScotslad";
const CONTACT_EMAIL = "hello@dtscotslad.com";
const EFFECTIVE_DATE = "27 July 2026";

function Section({ id, title, children }) {
    return (
        <section id={id} className="mb-12" data-testid={`terms-section-${id}`}>
            <h2 className="font-serif text-2xl lg:text-3xl tracking-tight mb-4 leading-tight">
                {title}
            </h2>
            <div className="space-y-4 text-[var(--sb-text-dim)] text-base leading-relaxed">
                {children}
            </div>
        </section>
    );
}

export default function Terms() {
    usePageMeta({
        title: `Terms & Conditions — ${SITE}`,
        description: `The terms that govern use of the ${SITE} website, subscriptions, bookings and user submissions.`,
    });

    return (
        <div className="relative max-w-4xl mx-auto px-6 lg:px-12 py-24 lg:py-32" data-testid="terms-page">
            {/* -- Header ------------------------------------------------- */}
            <header className="mb-16">
                <Reveal>
                    <div className="overline mb-4" style={{ color: "#0055A4", fontWeight: 700 }}>
                        <span className="editorial-rule" style={{ background: "#0055A4" }} />
                        Legal
                    </div>
                </Reveal>
                <h1
                    className="font-serif font-light tracking-tighter leading-[0.9]"
                    style={{ fontSize: "clamp(2.6rem, 6vw, 5.5rem)" }}
                    data-testid="terms-title"
                >
                    <div><MaskedLine delay={0.1}>Terms &amp;</MaskedLine></div>
                    <div>
                        <MaskedLine delay={0.3}>
                            <span className="italic" style={{ color: "#0055A4" }}>conditions.</span>
                        </MaskedLine>
                    </div>
                </h1>
                <Reveal delay={0.45}>
                    <p className="text-[var(--sb-text-dim)] mt-8 text-lg leading-relaxed">
                        These terms govern your use of {SITE} (this website). By using the site, subscribing, booking a flight or submitting content, you agree to them. Effective {EFFECTIVE_DATE}.
                    </p>
                </Reveal>
            </header>

            <Section id="about" title="1. About this site">
                <p>
                    {SITE} is a personal aerial-cinematography portfolio, blog and small subscription platform operated from Scotland. It shares drone films, stills and field notes, offers optional paid subscriptions and accepts commission bookings.
                </p>
                <p>
                    Contact for anything in this document: <a href={`mailto:${CONTACT_EMAIL}`} className="text-[var(--sb-brand)] hover:text-[var(--sb-text)]">{CONTACT_EMAIL}</a>.
                </p>
            </Section>

            <Section id="age" title="2. Age &amp; acceptable use">
                <p>
                    You must be at least 16 years old to create an account, subscribe, or submit content. If you&rsquo;re under 18, please have a parent or guardian&rsquo;s permission.
                </p>
                <p>You agree not to:</p>
                <ul className="list-disc pl-6 space-y-2">
                    <li>Use the site for anything unlawful, abusive or misleading.</li>
                    <li>Attempt to access private admin areas or interfere with the site&rsquo;s security.</li>
                    <li>Scrape, republish or re-sell films, photographs or copy from this site without written permission.</li>
                    <li>Submit content that infringes anyone else&rsquo;s rights or breaches UK drone / privacy law.</li>
                </ul>
            </Section>

            <Section id="subscriptions" title="3. Subscriptions">
                <p>
                    The £1.89 / month subscription unlocks subscriber-only films and lets you contribute to the Community Spotlight. Payments are processed by PayPal — {SITE} never sees or stores your card details.
                </p>
                <p>
                    Subscriptions renew monthly until cancelled. You can cancel anytime from your PayPal account; access continues until the end of the paid period. Refunds outside statutory rights are at our discretion; please email us if something has gone wrong.
                </p>
            </Section>

            <Section id="bookings" title="4. Bookings &amp; commissions">
                <p>
                    Booking a shoot through the site is a request, not a confirmed contract. A booking is only confirmed once we&rsquo;ve replied by email agreeing scope, date, location and fees.
                </p>
                <p>
                    All flights are operated under UK Civil Aviation Authority (CAA) rules. Some locations, weather conditions or airspace restrictions may make a shoot unsafe or unlawful; where that happens we&rsquo;ll always propose an alternative date or refund.
                </p>
            </Section>

            <Section id="submissions" title="5. User submissions">
                <p>
                    When you submit a video (Community Spotlight), a testimonial or a Visitor Voice Log, you confirm that:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                    <li>You created the content, or you have permission from everyone in it.</li>
                    <li>It doesn&rsquo;t contain unlawful, abusive, misleading or private content about other people.</li>
                    <li>You&rsquo;re happy for us to display it publicly on the site alongside your first name and, where relevant, the location.</li>
                </ul>
                <p>
                    You keep all copyright in what you send us. By submitting, you grant {SITE} a non-exclusive, worldwide, royalty-free licence to host, display and lightly edit your submission for use on this site. We can decline or remove any submission at any time, for any reason.
                </p>
            </Section>

            <Section id="ip" title="6. Our copyright">
                <p>
                    All films, stills, text and design on {SITE} are © Mark unless credited otherwise. You&rsquo;re welcome to share links to public pages. Please don&rsquo;t re-upload the films, republish the writing or use any imagery commercially without written permission.
                </p>
                <p>
                    For licensing enquiries, contact <a href={`mailto:${CONTACT_EMAIL}`} className="text-[var(--sb-brand)] hover:text-[var(--sb-text)]">{CONTACT_EMAIL}</a>.
                </p>
            </Section>

            <Section id="privacy" title="7. Personal data &amp; privacy">
                <p>
                    We collect the minimum data needed to run the site: your email if you subscribe to the newsletter, your account details if you sign in, and any content you submit. We use this data solely to operate the site and communicate with you about it.
                </p>
                <p>
                    We use third-party services that handle their own data — PayPal (payments), Resend (email alerts), YouTube (video embeds), Leaflet / OpenStreetMap (maps). Their own privacy policies apply when you interact with them.
                </p>
                <p>
                    You can request a copy of your data or delete your account by emailing <a href={`mailto:${CONTACT_EMAIL}`} className="text-[var(--sb-brand)] hover:text-[var(--sb-text)]">{CONTACT_EMAIL}</a>. We aim to respond within 14 days.
                </p>
            </Section>

            <Section id="voicelogs" title="8. Voice logs &amp; recordings">
                <p>
                    Visitor Voice Logs are audio recordings you send us voluntarily using your device&rsquo;s microphone. We only record when you tap the record button; nothing is captured before then. Recordings are moderated before appearing publicly.
                </p>
                <p>
                    Please don&rsquo;t include personal information about other people in voice logs. If a recording contains someone&rsquo;s identifiable details without permission, we&rsquo;ll decline it.
                </p>
            </Section>

            <Section id="third-party" title="9. Third-party links">
                <p>
                    The site may link out to other websites. We&rsquo;re not responsible for their content, availability or practices. Follow those links at your own discretion.
                </p>
            </Section>

            <Section id="disclaimer" title="10. Disclaimer &amp; liability">
                <p>
                    The site is provided &ldquo;as is&rdquo;. We do our best to keep it accurate, safe and available, but we can&rsquo;t guarantee uninterrupted service or that everything is free from errors.
                </p>
                <p>
                    To the fullest extent permitted by law, {SITE} isn&rsquo;t liable for indirect or consequential losses arising from your use of the site. Nothing in these terms limits any liability that can&rsquo;t be excluded under UK consumer law.
                </p>
            </Section>

            <Section id="changes" title="11. Changes to these terms">
                <p>
                    We may update these terms occasionally. The effective date at the top will reflect the latest version. If a change is significant, we&rsquo;ll flag it on the site or by email to subscribers.
                </p>
            </Section>

            <Section id="law" title="12. Governing law">
                <p>
                    These terms are governed by the laws of Scotland. Any disputes will be dealt with by the courts of Scotland.
                </p>
            </Section>

            <Section id="contact" title="13. Contact">
                <p>
                    Questions, requests, complaints or takedowns: <a href={`mailto:${CONTACT_EMAIL}`} className="text-[var(--sb-brand)] hover:text-[var(--sb-text)]">{CONTACT_EMAIL}</a>. We reply as quickly as we can — usually within a few days.
                </p>
            </Section>

            <PageCTA variant="flight" className="!px-0" />
            <HighlandBackdrop variant="soft" />
        </div>
    );
}
